import React, { useRef, useEffect } from 'react';
import { Typography } from '@material-ui/core';
import { Button, Icon, Link, Checkbox } from '@saviynt/design-system';
import PropTypes from 'prop-types';
import ProgressIndicator from '../shared/ProgressIndicator';
import FilterBar from './FilterBar';
import { STATUS } from '../../constants/migrationConstants';
import {
  UI_LABELS,
  OBJECT_TYPE_LABELS,
  STATUS_LABELS,
  ATTRIBUTE_MAPPING_LABELS,
} from '../../constants/stringConstants';
import useStyles from './styles/ObjectListView.styles';

const parseMetadata = (metadataStr) => {
  try {
    return metadataStr ? JSON.parse(metadataStr) : {};
  } catch {
    return {};
  }
};

const formatDate = (timestamp) => {
  if (!timestamp) return null;
  return new Date(timestamp).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const ObjectListView = ({
  objects,
  sourceType,
  selectedCount,
  isSelected,
  onSelectAll,
  onObjectSelect,
  onBack,
  onPreview,
  migrationProgress,
  objectTypeLabel,
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusChange,
  sortBy,
  onSortByChange,
  sortOrder,
  onSortOrderChange,
  filteredCount,
  totalCount,
  automatedMigration,
  migrationDisabled,
  userAttributeMappingConfigured,
}) => {
  const classes = useStyles();
  const listEndRef = useRef(null);
  const prevCompletedCount = useRef(null);
  const isAutomated =
    automatedMigration?.some((item) => item.type === objectTypeLabel.replace(/\s/g, '')) || false;
  const requiresAttributeMapping =
    objectTypeLabel === OBJECT_TYPE_LABELS.APPLICATION ||
    objectTypeLabel === OBJECT_TYPE_LABELS.BUNDLE ||
    objectTypeLabel === OBJECT_TYPE_LABELS.ENTERPRISE_ROLES;
  const isMigrationBlocked = requiresAttributeMapping && !userAttributeMappingConfigured;
  const availableCount = objects.filter((obj) => obj.status !== STATUS.STATUS_COMPLETED).length;
  const completedCount = objects.filter((obj) => obj.status === STATUS.STATUS_COMPLETED).length;
  const isAllSelected = selectedCount === availableCount && availableCount > 0;

  useEffect(() => {
    if (
      prevCompletedCount.current !== null &&
      completedCount > prevCompletedCount.current &&
      listEndRef.current
    ) {
      setTimeout(() => {
        listEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 300);
    }
    prevCompletedCount.current = completedCount;
  }, [completedCount]);

  return (
    <div>
      <div className={classes.header}>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link kind="button" onClick={onBack} prefixIconKind="ChevronLeft" size="Large" />
        <div>
          <Typography variant="h5" className={classes.title}>
            {objectTypeLabel}
          </Typography>
          <Typography variant="body2" className={classes.subtitle}>
            {UI_LABELS.SELECT_OBJECTS}
          </Typography>
          {isMigrationBlocked && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                padding: '0.5rem 1rem',
                backgroundColor: '#fef2f2',
                border: '1px solid #fca5a5',
                borderRadius: '8px',
                marginTop: '0.5rem',
              }}>
              <Icon
                kind="AlertCriticalFilledSeparateColors"
                label="Warning"
                size="small"
                color="--color-critical-700"
              />
              <Typography style={{ fontSize: '0.8125rem', color: '#dc2626', fontWeight: 600 }}>
                {ATTRIBUTE_MAPPING_LABELS.USER_ATTRIBUTE_NOT_SAVED_CONFIG_DATA}
              </Typography>
            </div>
          )}
          {objectTypeLabel === OBJECT_TYPE_LABELS.BUNDLE && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                padding: '0.5rem 1rem',
                backgroundColor: '#eff6ff',
                border: '1px solid #93c5fd',
                borderRadius: '8px',
                marginTop: '0.5rem',
              }}>
              <Icon
                kind="AlertInfo"
                label="Info"
                size="small"
                color="--color-foreground-info-subtle"
              />
              <Typography style={{ fontSize: '0.8125rem', color: '#1d4ed8', fontWeight: 600 }}>
                {UI_LABELS.BUNDLE_PREREQUISITE}
              </Typography>
            </div>
          )}
        </div>
      </div>

      {totalCount > 0 && (
        <FilterBar
          searchTerm={searchTerm}
          onSearchChange={onSearchChange}
          statusFilter={statusFilter}
          onStatusChange={onStatusChange}
          sortBy={sortBy}
          onSortByChange={onSortByChange}
          sortOrder={sortOrder}
          onSortOrderChange={onSortOrderChange}
          filteredCount={filteredCount}
          totalCount={totalCount}
          showSorting={!sourceType?.includes('oim')}
          showStatusFilter={isAutomated}
        />
      )}

      {isAutomated && (
        <div className={classes.selectAllContainer}>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className={classes.selectAllLabel}>
            <Checkbox
              isChecked={isAllSelected}
              isDisabled={isMigrationBlocked}
              onChange={() => onSelectAll(!isAllSelected)}
            />
            {UI_LABELS.SELECT_AVAILABLE} ({selectedCount})
          </label>
        </div>
      )}

      {objects.length === 0 && totalCount > 0 && (
        <div className={classes.emptyState}>
          <div className={classes.emptyStateIcon}>
            <Icon
              kind="Search"
              color="--color-foreground-neutral-inverse"
              clabel="Search"
              size="large"
            />
          </div>
          <Typography variant="h6" className={classes.emptyStateText}>
            {UI_LABELS.NO_OBJECTS_FOUND}
          </Typography>
        </div>
      )}

      {objects.length > 0 && (
        <div
          className={`${classes.objectList} ${
            selectedCount > 0 ? classes.objectListWithSelection : ''
          }`}>
          {objects.map((object) => {
            const meta = parseMetadata(object.metadata);
            const isOIM = sourceType?.includes('oim');
            const connectorType = meta.type ?? null;
            const isDisconnected = isOIM ? !!meta.disconnected : false;
            const createdDate = formatDate(meta.created);
            const modifiedDate = formatDate(meta.modified);

            const isCompleted = object.status === STATUS.STATUS_COMPLETED;
            const isFailed = object.status === STATUS.STATUS_FAILED;
            const blockedByParent = migrationDisabled[object.id];
            const isDisabledByParent = !!blockedByParent;

            const cardClasses = [
              classes.objectCard,
              isCompleted && classes.objectCardCompleted,
              isFailed && classes.objectCardFailed,
              isSelected(object.id) && classes.objectCardSelected,
              isDisabledByParent && classes.objectCardDisabled,
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <div key={object.id} className={cardClasses}>
                <div className={classes.objectCardContent}>
                  {isAutomated && (
                    <Checkbox
                      isChecked={isSelected(object.id)}
                      isDisabled={isCompleted || isDisabledByParent || isMigrationBlocked}
                      onChange={() => onObjectSelect(object.id)}
                    />
                  )}

                  <div className={classes.objectDetails}>
                    <div className={classes.objectName}>
                      <Typography className={classes.objectNameText}>
                        {object.objectName}
                      </Typography>
                    </div>

                    {object.id && (
                      <div className={classes.objectId}>
                        <Typography className={classes.fieldLabel}>{UI_LABELS.ID}</Typography>
                        <Typography className={classes.fieldValue}>{object.id}</Typography>
                      </div>
                    )}

                    {connectorType && (
                      <div className={classes.objectConnector}>
                        <Typography className={classes.fieldLabel}>{UI_LABELS.TYPE}</Typography>
                        <Typography className={classes.fieldValueWrap}>{connectorType}</Typography>
                      </div>
                    )}

                    {createdDate && (
                      <div className={classes.objectDate}>
                        <Typography className={classes.fieldLabel}>{UI_LABELS.CREATED}</Typography>
                        <Typography className={classes.fieldValue}>{createdDate}</Typography>
                      </div>
                    )}

                    {modifiedDate && (
                      <div className={classes.objectDate}>
                        <Typography className={classes.fieldLabel}>{UI_LABELS.MODIFIED}</Typography>
                        <Typography className={classes.fieldValue}>{modifiedDate}</Typography>
                      </div>
                    )}
                  </div>

                  <Button
                    kind="outlined"
                    size="small"
                    onClick={(e) => {
                      e?.stopPropagation?.();
                      onPreview(object, 'source');
                    }}
                    className={classes.previewButton}>
                    {isOIM ? UI_LABELS.OIM : UI_LABELS.SAILPOINT} {UI_LABELS.CONFIG_PREVIEW}
                  </Button>
                </div>

                {isAutomated && (
                  <div className={classes.statusContainer}>
                    {isCompleted && (
                      <Typography className={`${classes.statusText} ${classes.statusSuccess}`}>
                        <Icon
                          kind="CheckCircleFilledSeparateColors"
                          label="Circle"
                          color="--color-foreground-success-subtle"
                          size="small"
                        />
                        {UI_LABELS.MIGRATION_COMPLETE_NO_REMIGRATE}
                      </Typography>
                    )}
                    {object.status === STATUS.STATUS_PENDING &&
                      !isDisconnected &&
                      !isDisabledByParent &&
                      objectTypeLabel === OBJECT_TYPE_LABELS.BUNDLE &&
                      connectorType?.toLowerCase() === 'business' && (
                        <Typography className={`${classes.statusText} ${classes.statusInfo}`}>
                          <Icon
                            kind="AlertInfo"
                            label="Info"
                            color="--color-foreground-info-subtle"
                            size="small"
                          />
                          {UI_LABELS.PARENT_BUNDLE}
                        </Typography>
                      )}
                    {object.status === STATUS.STATUS_PENDING &&
                      !isDisconnected &&
                      !isDisabledByParent && (
                        <Typography className={`${classes.statusText} ${classes.statusWarning}`}>
                          <Icon
                            kind="time"
                            label="Circle"
                            color="--color-foreground-warning-subtle"
                            size="small"
                          />
                          {UI_LABELS.READY_TO_MIGRATE}
                        </Typography>
                      )}
                    {isDisabledByParent && (
                      <Typography className={`${classes.statusText} ${classes.statusError}`}>
                        <Icon
                          kind="AlertCriticalFilledSeparateColors"
                          label="Critical Alert"
                          color="--color-foreground-critical-medium"
                          size="small"
                        />
                        {`Pre-requisite: Migrate Parent Application (${blockedByParent})`}
                      </Typography>
                    )}
                    {isDisconnected && (
                      <Typography className={`${classes.statusText} ${classes.statusError}`}>
                        <Icon
                          kind="AlertCriticalFilledSeparateColors"
                          label="Critical Alert"
                          color="--color-foreground-critical-medium"
                          size="small"
                        />
                        {UI_LABELS.DISCONNECTED_APPLICATION}
                      </Typography>
                    )}
                    {object.status === STATUS.STATUS_FAILED && (
                      <Typography className={`${classes.statusText} ${classes.statusError}`}>
                        <Icon
                          kind="AlertCriticalFilledSeparateColors"
                          label="Critical Alert"
                          color="--color-foreground-critical-medium"
                          size="small"
                        />
                        {UI_LABELS.MIGRATION_FAILED}
                      </Typography>
                    )}
                  </div>
                )}

                {migrationProgress[object.id] && (
                  <div
                    className={`${classes.progressContainer} ${
                      isAutomated ? classes.progressContainerAutomated : ''
                    }`}>
                    <ProgressIndicator
                      message={migrationProgress[object.id].message}
                      variant={(() => {
                        const { status } = migrationProgress[object.id];
                        if (status === STATUS_LABELS.COMPLETED) return UI_LABELS.SUCCESS;
                        if (status === STATUS_LABELS.FAILED) return UI_LABELS.ERROR;
                        return UI_LABELS.INFO;
                      })()}
                      showProgress={migrationProgress[object.id].status === 'IN_PROGRESS'}
                    />
                  </div>
                )}
              </div>
            );
          })}
          <div ref={listEndRef} />
        </div>
      )}
    </div>
  );
};

ObjectListView.propTypes = {
  objects: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  sourceType: PropTypes.string.isRequired,
  selectedCount: PropTypes.number.isRequired,
  isSelected: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onObjectSelect: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onPreview: PropTypes.func.isRequired,
  migrationProgress: PropTypes.shape({}).isRequired,
  objectTypeLabel: PropTypes.string.isRequired,
  searchTerm: PropTypes.string,
  onSearchChange: PropTypes.func,
  statusFilter: PropTypes.string,
  onStatusChange: PropTypes.func,
  sortBy: PropTypes.string,
  onSortByChange: PropTypes.func,
  sortOrder: PropTypes.string,
  onSortOrderChange: PropTypes.func,
  filteredCount: PropTypes.number,
  totalCount: PropTypes.number,
  automatedMigration: PropTypes.arrayOf(PropTypes.shape({})),
  migrationDisabled: PropTypes.shape({}),
  userAttributeMappingConfigured: PropTypes.bool,
};

ObjectListView.defaultProps = {
  searchTerm: '',
  onSearchChange: undefined,
  statusFilter: '',
  onStatusChange: undefined,
  sortBy: '',
  onSortByChange: undefined,
  sortOrder: '',
  onSortOrderChange: undefined,
  filteredCount: 0,
  totalCount: 0,
  automatedMigration: [],
  migrationDisabled: {},
  userAttributeMappingConfigured: true,
};

export default ObjectListView;
